/* Terachat · fundo animado do login: Particulas
 * Bokeh verde em WebGL com foco que respira; redemoinho no cursor/dedo.
 * Componente autocontido, sem dependências além do React.
 * Pausa com a aba oculta, respeita prefers-reduced-motion, limita o DPR e baixa
 * a resolução sozinho se o aparelho não segurar ~40fps.
 *
 * Uso:  <ParticulasBackground />   (fixed, cobre a tela, z-index 0; ponha o card por cima)
 */
import { useEffect, useRef, type CSSProperties } from 'react'

const NAVY = '#02172C'

// ---------- infra ----------
type Frame<S> = (st: S, w: number, h: number, dpr: number, t: number, dt: number) => void

interface LoopOptions<S> {
  maxDpr?: number
  minDpr?: number
  setup?: (st: S, w: number, h: number, dpr: number) => void
  frame: Frame<S>
}

// RAF com: DPR limitado, pausa com aba oculta, quadro estático em reduced-motion
// e queda automática de resolução se a média ficar abaixo de ~40fps.
function runLoop<S>(canvas: HTMLCanvasElement, st: S, { maxDpr = 2, minDpr = 1, setup, frame }: LoopOptions<S>) {
  let dpr = Math.min(window.devicePixelRatio || 1, maxDpr)
  let w = 1, h = 1, raf = 0, last = 0, t = 0, alive = true
  let sum = 0, n = 0
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

  const resize = () => {
    const r = canvas.getBoundingClientRect()
    w = Math.max(1, r.width); h = Math.max(1, r.height)
    canvas.width = Math.max(1, Math.round(w * dpr))
    canvas.height = Math.max(1, Math.round(h * dpr))
    setup?.(st, w, h, dpr)
    if (reduce) frame(st, w, h, dpr, 18, 0)
  }
  const tick = (now: number) => {
    if (!alive) return
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.016
    last = now; t += dt
    frame(st, w, h, dpr, t, dt)
    sum += dt; n++
    if (n === 90) {
      const avg = sum / n; sum = 0; n = 0
      if (avg > 1 / 40 && dpr > minDpr) { dpr = Math.max(minDpr, dpr - 0.25); resize() }
    }
    raf = requestAnimationFrame(tick)
  }
  const start = () => { if (!raf && !reduce && alive) { last = 0; raf = requestAnimationFrame(tick) } }
  const stop = () => { cancelAnimationFrame(raf); raf = 0 }
  const onVis = () => (document.hidden ? stop() : start())
  const ro = new ResizeObserver(resize)
  ro.observe(canvas)
  document.addEventListener('visibilitychange', onVis)
  resize()
  start()
  return {
    destroy() { alive = false; stop(); ro.disconnect(); document.removeEventListener('visibilitychange', onVis) },
  }
}

interface Pointer {
  x: number; y: number; a: number   // posição suavizada (0..1, y para baixo) e presença
  rx: number; ry: number            // posição crua
  vx: number; vy: number            // velocidade em "telas menores por segundo" (y para baixo)
  step(dt: number): void; off(): void
}

// ponteiro relativo ao canvas; funciona com mouse e toque
function pointer(canvas: HTMLCanvasElement): Pointer {
  let tx = 0.5, ty = 0.5, ta = 0, lastT = 0, lastX = 0, lastY = 0, idle = 1
  const move = (e: PointerEvent) => {
    const r = canvas.getBoundingClientRect()
    const x = e.clientX - r.left, y = e.clientY - r.top
    tx = x / r.width; ty = y / r.height; ta = 1
    p.rx = tx; p.ry = ty
    const dts = (e.timeStamp - lastT) / 1000
    if (e.type === 'pointermove' && lastT && dts > 0.002 && dts < 0.2) {
      const m = Math.min(r.width, r.height) || 1
      const ivx = (x - lastX) / m / dts, ivy = (y - lastY) / m / dts
      p.vx = p.vx * 0.55 + ivx * 0.45; p.vy = p.vy * 0.55 + ivy * 0.45
      const sp = Math.hypot(p.vx, p.vy)
      if (sp > 6) { p.vx *= 6 / sp; p.vy *= 6 / sp }
    }
    lastT = e.timeStamp; lastX = x; lastY = y; idle = 0
  }
  const leave = () => { ta = 0 }
  const up = (e: PointerEvent) => { if (e.pointerType === 'touch') leave() }
  window.addEventListener('pointermove', move, { passive: true })
  window.addEventListener('pointerdown', move, { passive: true })
  window.addEventListener('pointerup', up, { passive: true })
  document.addEventListener('pointerleave', leave)
  const p: Pointer = {
    x: 0.5, y: 0.5, a: 0, rx: 0.5, ry: 0.5, vx: 0, vy: 0,
    step(dt) {
      const k = 1 - Math.exp(-dt * 4)
      p.x += (tx - p.x) * k; p.y += (ty - p.y) * k; p.a += (ta - p.a) * (1 - Math.exp(-dt * 2))
      idle += dt
      if (idle > 0.06) { const d = Math.exp(-dt * 14); p.vx *= d; p.vy *= d } // parou de mexer: zera rápido
    },
    off() {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerdown', move)
      window.removeEventListener('pointerup', up)
      document.removeEventListener('pointerleave', leave)
    },
  }
  return p
}

// ---------- vento ----------
// Grade pequena em espaço de tela. O cursor sopra na direção em que se move; o vento
// perde força rápido (arrasto) e o deslocamento volta devagar (retorno), como areia
// assentando. Unidades: fração do lado menor do canvas. Linha 0 = base (y para cima).
const WIND = {
  cells: 48,      // células no lado menor
  sigma: 0.1,     // raio do sopro (bolhas são grandes)
  gain: 0.45,     // fração da velocidade do cursor que vira vento
  spread: 0.18,   // empurrão para os lados do rastro, proporcional à velocidade
  drag: 3.2,      // 1/s: quão rápido o vento morre
  ret: 0.5,       // 1/s: quão rápido a areia volta (0.5 -> ~2s para assentar)
  diffuse: 4,     // 1/s: quanto o deslocamento se espalha para as vizinhas
  max: 0.16,      // deslocamento máximo
}

class WindField {
  cols = 1; rows = 1
  d = new Float32Array(2); v = new Float32Array(2); tmp = new Float32Array(2)
  bytes = new Uint8Array(4)
  active = false
  resize(w: number, h: number) {
    const m = Math.min(w, h)
    this.cols = Math.max(4, Math.round((WIND.cells * w) / m)); this.rows = Math.max(4, Math.round((WIND.cells * h) / m))
    const n = this.cols * this.rows
    this.d = new Float32Array(n * 2); this.v = new Float32Array(n * 2); this.tmp = new Float32Array(n * 2)
    this.bytes = new Uint8Array(n * 4).fill(128)
    for (let i = 3; i < this.bytes.length; i += 4) { this.bytes[i - 1] = 0; this.bytes[i] = 255 }
    this.active = true
  }
  /** cx, cy em 0..1 (y para cima); vx, vy em lados-menores/s (y para cima). Retorna se algo mudou. */
  step(dt: number, w: number, h: number, cx: number, cy: number, vx: number, vy: number): boolean {
    const { cols, rows, d, v, tmp } = this
    const speed = Math.hypot(vx, vy)
    if (!this.active && speed < 0.02) return false
    const m = Math.min(w, h), sx = w / m, sy = h / m
    const s2 = 2 * WIND.sigma * WIND.sigma, pull = Math.min(1, dt * 10)
    const kDrag = Math.exp(-dt * WIND.drag), kRet = Math.exp(-dt * WIND.ret)
    let peak = 0
    for (let j = 0, i = 0; j < rows; j++) {
      const dy = ((j + 0.5) / rows - cy) * sy
      for (let c = 0; c < cols; c++, i += 2) {
        if (speed >= 0.02) {
          const dx = ((c + 0.5) / cols - cx) * sx
          const r2 = dx * dx + dy * dy
          if (r2 < s2 * 4.6) { // até ~3 sigma
            const fall = Math.exp(-r2 / s2), r = Math.sqrt(r2) + 1e-4
            const tx = vx * WIND.gain + (dx / r) * speed * WIND.spread
            const ty = vy * WIND.gain + (dy / r) * speed * WIND.spread
            v[i] += (tx - v[i]) * fall * pull; v[i + 1] += (ty - v[i + 1]) * fall * pull
          }
        }
        v[i] *= kDrag; v[i + 1] *= kDrag
        let px = (d[i] + v[i] * dt) * kRet, py = (d[i + 1] + v[i + 1] * dt) * kRet
        const l = Math.hypot(px, py)
        if (l > WIND.max) { px *= WIND.max / l; py *= WIND.max / l }
        d[i] = px; d[i + 1] = py
        if (l > peak) peak = l
      }
    }
    // difusão: deslocamento escorre para as vizinhas (o "monte" se espalha)
    const kd = (1 - Math.exp(-dt * WIND.diffuse)) * 0.5
    tmp.set(d)
    for (let j = 0, i = 0; j < rows; j++) {
      for (let c = 0; c < cols; c++, i += 2) {
        const l = c > 0 ? i - 2 : i, r = c < cols - 1 ? i + 2 : i
        const u = j < rows - 1 ? i + cols * 2 : i, b = j > 0 ? i - cols * 2 : i
        d[i] += ((tmp[l] + tmp[r] + tmp[u] + tmp[b]) * 0.25 - tmp[i]) * kd
        d[i + 1] += ((tmp[l + 1] + tmp[r + 1] + tmp[u + 1] + tmp[b + 1]) * 0.25 - tmp[i + 1]) * kd
      }
    }
    const wasActive = this.active
    this.active = peak > 2e-4 || speed >= 0.02
    if (!this.active) { d.fill(0); v.fill(0) }
    if (!this.active && !wasActive) return false
    const bytes = this.bytes, inv = 127 / WIND.max
    for (let i = 0, o = 0; i < d.length; i += 2, o += 4) {
      bytes[o] = 128 + Math.round(d[i] * inv); bytes[o + 1] = 128 + Math.round(d[i + 1] * inv)
      bytes[o + 2] = Math.min(255, Math.round((Math.hypot(d[i], d[i + 1]) / WIND.max) * 255))
    }
    return true
  }
}

// ---------- helper WebGL: fundo em shader + nuvem de pontos calculada na GPU ----------
interface GlPointsOptions {
  count: (cssWidth: number) => number
  gen: (data: Float32Array, offset: number) => void // preenche 4 floats por partícula
  vs: string
  fs: string
  bg: string
  maxDpr?: number
  minDpr?: number
}

function glPoints(canvas: HTMLCanvasElement, { count, gen, vs, fs, bg, maxDpr = 2, minDpr = 1 }: GlPointsOptions): () => void {
  const gl = canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' })
  if (!gl) return () => { } // sem WebGL: fica o fundo navy do container

  const ptr = pointer(canvas)
  const sh = (type: number, src: string) => {
    const o = gl.createShader(type)!
    gl.shaderSource(o, src); gl.compileShader(o)
    if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(o) ?? 'shader')
    return o
  }
  const prog = (v: string, f: string) => {
    const p = gl.createProgram()!
    gl.attachShader(p, sh(gl.VERTEX_SHADER, v)); gl.attachShader(p, sh(gl.FRAGMENT_SHADER, f)); gl.linkProgram(p)
    return p
  }

  // vento precisa de textura no vertex shader (quase todo aparelho tem; sem ela, fica sem interação)
  const windOn = (gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS) as number) > 0
  const bgP = prog('attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}', bg)
  const ptP = prog(vs.replace('__WIND_SAMPLE__', windOn ? 'texture2D(F,gl_Position.xy*.5+.5)' : 'vec4(128./255.,128./255.,0.,1.)'), fs)

  const wind = new WindField()
  const windTex = gl.createTexture()
  let texCols = 0, texRows = 0
  gl.bindTexture(gl.TEXTURE_2D, windTex)
  for (const [k, v] of [[gl.TEXTURE_MIN_FILTER, gl.LINEAR], [gl.TEXTURE_MAG_FILTER, gl.LINEAR], [gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE]])
    gl.texParameteri(gl.TEXTURE_2D, k, v)
  const quad = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, quad)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW)
  const n = count(canvas.getBoundingClientRect().width)
  const data = new Float32Array(n * 4)
  for (let i = 0; i < n; i++) gen(data, i * 4)
  const pts = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, pts)
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW)
  const maxPt = (gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE) as Float32Array)[1]
  const U = (p: WebGLProgram, k: string) => gl.getUniformLocation(p, k)
  const bgU = { R: U(bgP, 'R'), T: U(bgP, 'T'), M: U(bgP, 'M') }
  const ptU = { R: U(ptP, 'R'), T: U(ptP, 'T'), M: U(ptP, 'M'), D: U(ptP, 'D'), S: U(ptP, 'S') }
  const aBg = gl.getAttribLocation(bgP, 'p'), aPt = gl.getAttribLocation(ptP, 'a')
  gl.useProgram(ptP)
  gl.uniform1f(U(ptP, 'MX'), WIND.max)
  gl.uniform1i(U(ptP, 'F'), 0)

  const loop = runLoop<null>(canvas, null, {
    maxDpr, minDpr,
    frame(_st, w, h, dpr, t, dt) {
      ptr.step(dt || 0.016)
      const T = t * 0.6 + 30
      gl.viewport(0, 0, canvas.width, canvas.height)

      // simula o vento e sobe a textura só quando algo mudou
      if (windOn) {
        if (wind.cols * wind.rows <= 1 || Math.abs(wind.cols / wind.rows - w / h) > 0.05) wind.resize(w, h)
        const changed = wind.step(Math.min(dt || 0, 0.05), w, h, ptr.rx, 1 - ptr.ry, ptr.vx, -ptr.vy)
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, windTex)
        if (texCols !== wind.cols || texRows !== wind.rows) {
          texCols = wind.cols; texRows = wind.rows
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, texCols, texRows, 0, gl.RGBA, gl.UNSIGNED_BYTE, wind.bytes)
        } else if (changed) {
          gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, texCols, texRows, gl.RGBA, gl.UNSIGNED_BYTE, wind.bytes)
        }
      }

      gl.disable(gl.BLEND)
      gl.useProgram(bgP)
      gl.bindBuffer(gl.ARRAY_BUFFER, quad)
      gl.enableVertexAttribArray(aBg); gl.vertexAttribPointer(aBg, 2, gl.FLOAT, false, 0, 0)
      gl.uniform2f(bgU.R, canvas.width, canvas.height); gl.uniform1f(bgU.T, T); gl.uniform3f(bgU.M, ptr.x, 1 - ptr.y, ptr.a)
      gl.drawArrays(gl.TRIANGLES, 0, 3)
      gl.disableVertexAttribArray(aBg)

      gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE) // aditivo
      gl.useProgram(ptP)
      gl.bindBuffer(gl.ARRAY_BUFFER, pts)
      gl.enableVertexAttribArray(aPt); gl.vertexAttribPointer(aPt, 4, gl.FLOAT, false, 0, 0)
      gl.uniform2f(ptU.R, canvas.width, canvas.height); gl.uniform1f(ptU.T, T)
      gl.uniform3f(ptU.M, ptr.x, 1 - ptr.y, ptr.a); gl.uniform1f(ptU.D, dpr); gl.uniform1f(ptU.S, maxPt)
      gl.drawArrays(gl.POINTS, 0, n)
      gl.disableVertexAttribArray(aPt)
    },
  })
  return () => { loop.destroy(); ptr.off(); gl.getExtension('WEBGL_lose_context')?.loseContext() }
}

const GLSL_HASH = 'float h(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}'

// ---------- efeito ----------
export function mountParticulas(canvas: HTMLCanvasElement): () => void {
  const rnd = Math.random;
  return glPoints(canvas, {
    maxDpr: 2, minDpr: 1,
    count: (w: number) => (w < 640 ? 50 : 150),
    gen(d: Float32Array, o: number) { d[o] = rnd() * 2 - 1; d[o + 1] = rnd() * 2 - 1; d[o + 2] = rnd(); d[o + 3] = rnd(); },
    vs: `precision highp float;
attribute vec4 a; uniform vec2 R; uniform float T,D,S,MX; uniform vec3 M; uniform sampler2D F;
varying vec4 vC; varying float vB;
void main(){
float s=a.w, z=a.z;                                 // z: 0 perto, 1 longe
float asp=R.x/R.y;
float sp=.018+.03*s;
float y=mod(a.y+1.+T*sp,2.4)-1.2;                   // sobe devagar e reaparece embaixo
float x=a.x*asp*1.1 + sin(T*(.15+s*.2)+s*40.)*.06*min(asp,1.);
float sc=1./(.55+z*1.4);
vec2 par=(M.xy-.5)*M.z*(.12*(1.-z));               // paralaxe pelo dedo/mouse
vec2 p=vec2(x,y)*sc+par*vec2(asp,1.);
gl_Position=vec4(p.x/asp, p.y, 0.,1.);
// vento: cada bolha reage com força e ângulo próprios; as de perto voam mais (profundidade)
vec4 fw=__WIND_SAMPLE__;
vec2 wd=(fw.xy*255.-128.)/127.*MX; float wm=fw.z;
float wg=(.45+1.1*fract(s*91.7))*(1.15-z*.7), wa=(fract(s*53.3)-.5)*1.2;
vec2 dd=mat2(cos(wa),sin(wa),-sin(wa),cos(wa))*wd*wg;
dd+=vec2(sin(T*2.3+s*311.),cos(T*1.9+s*173.))*length(wd)*.12;
gl_Position.xy+=dd*min(R.x,R.y)/(R*.5);
float focus=.42+.18*sin(T*.12);
float b=clamp(abs(z-focus)*2.2,0.,1.);
vB=b;
float base=min(R.x,R.y)/D/400.;                    // escala com a tela
float hero=step(.93,s);                            // ~7% maiores e mais vivas
float sz=(4.+b*b*(38.+hero*60.))*sc*D*base*(.6+.7*s);
gl_PointSize=min(sz*(1.+wm*.2),S);
vec3 lime=vec3(.25,.84,.11), pale=vec3(.72,.95,.42), teal=vec3(.10,.72,.55);
vec3 c=mix(lime,pale,smoothstep(.55,1.,s)); c=mix(c,teal,smoothstep(.25,0.,s));
float tw=.75+.25*sin(T*(.8+s*2.)+s*20.);
float edge=smoothstep(1.2,.9,abs(y));
vC=vec4(mix(c,vec3(.85,1.,.7),wm*.3),(1./(1.+b*b*5.))*tw*edge*(1.-z*.45)*(1.+hero*.6)*(1.+wm*1.6));
}`,
    fs: `precision mediump float; varying vec4 vC; varying float vB;
void main(){
float d=length(gl_PointCoord-.5)*2.; if(d>1.)discard;
float sharp=exp(-d*d*10.)+exp(-d*d*2.5)*.25;       // ponto em foco: núcleo + halo
float disc=smoothstep(1.,.86,d);
float rim=smoothstep(.6,.95,d)*disc;
float a=mix(sharp, disc*.5+rim*.45, smoothstep(.08,.5,vB));
gl_FragColor=vec4(mix(vC.rgb,vec3(1.),sharp*(1.-vB)*.35)*(1.+.35*rim), vC.a*a);
}`,
    bg: `precision mediump float; uniform vec2 R; uniform float T; uniform vec3 M; ${GLSL_HASH}
void main(){
vec2 uv=gl_FragCoord.xy/R;
vec3 col=mix(vec3(.004,.05,.09),vec3(.008,.09,.172),uv.y);
float g=exp(-pow(length((uv-vec2(.5,.08))*vec2(R.x/R.y,1.)),2.)*3.);
col+=vec3(.08,.30,.10)*g*.35;
col*=1.-length(uv-.5)*.55;
col+=(h(gl_FragCoord.xy+fract(T)*37.)-.5)*.02;
gl_FragColor=vec4(col,1.);
}`,
  });
}

// ---------- componente ----------
type Props = { className?: string; style?: CSSProperties }

// Cada montagem cria o próprio <canvas>: assim o double-mount do StrictMode
// não reaproveita um contexto WebGL já destruído.
export default function ParticulasBackground({ className, style }: Props) {
  const host = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = host.current
    if (!el) return
    const canvas = document.createElement('canvas')
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block'
    el.appendChild(canvas)
    const destroy = mountParticulas(canvas)
    return () => { destroy(); canvas.remove() }
  }, [])

  return (
    <div
      ref={host}
      aria-hidden="true"
      className={className}
      style={{ position: 'fixed', inset: 0, zIndex: 0, background: NAVY, ...style }}
    />
  )
}
